// Mirrors the mobile Measurements History screen. Source of truth:
// bia-fit-rn-mobile src/modules/users/utils/measurements.utils.ts and
// components/measurements/MeasurementHistoryItem.

import {
  MEASUREMENT_DISPLAY_ORDER,
  MEASUREMENT_TYPE_LABELS,
  MEASUREMENT_TYPE_UNITS,
  MeasurementAttachment,
  MeasurementType,
  UserMeasurementLog,
} from "../types/measurement";
import {
  formatOneDecimalWithUnit,
  formatSignedOneDecimalWithUnit,
} from "./formatters";

export interface MeasurementDisplayRow {
  key: string;
  label: string;
  value: string;
  // Signed change vs the previous date, e.g. "+1.2 cm". Null when there is no
  // comparable earlier value or the rounded difference is zero.
  change: string | null;
  notes: string | null;
  attachments: MeasurementAttachment[];
}

export interface DatedMeasurementGroup {
  date: string; // YYYY-MM-DD
  byType: Partial<Record<MeasurementType, UserMeasurementLog[]>>;
}

// The backend backfills a value=0 placeholder for every type the user did not
// record on a date. Those are not real measurements and are never displayed.
const isRealMeasurement = (log: UserMeasurementLog): boolean =>
  parseFloat(log.value) > 0;

// measured_at is the source of truth for when a measurement was taken;
// created_at is the fallback. Sliced, not Date-parsed, so the calendar day
// matches the server's whereDate rather than the viewer's timezone.
const measurementDate = (log: UserMeasurementLog): string | null => {
  const timestamp = log.measured_at ?? log.created_at;
  return timestamp ? timestamp.split("T")[0] : null;
};

const chronologically = (
  a: UserMeasurementLog,
  b: UserMeasurementLog
): number => {
  const ta = a.measured_at ?? "";
  const tb = b.measured_at ?? "";
  if (ta !== tb) return ta < tb ? -1 : 1;
  return a.id - b.id;
};

/** Newest row of a type; arrays are chronological ascending. */
const latestOf = (
  rows?: UserMeasurementLog[]
): UserMeasurementLog | undefined => rows?.[rows.length - 1];

/**
 * Groups logs by calendar date, newest date first, keeping every row per type
 * so a genuine same-day re-measurement stays visible. Placeholder rows are
 * dropped before grouping, so a date of nothing but placeholders disappears.
 */
export const groupMeasurementsByDate = (
  logs: UserMeasurementLog[]
): DatedMeasurementGroup[] => {
  const byDate = new Map<string, UserMeasurementLog[]>();

  for (const log of logs) {
    if (!isRealMeasurement(log)) continue;
    const date = measurementDate(log);
    if (!date) continue;
    const bucket = byDate.get(date);
    if (bucket) bucket.push(log);
    else byDate.set(date, [log]);
  }

  const groups: DatedMeasurementGroup[] = [...byDate.entries()].map(
    ([date, rows]) => {
      const byType: Partial<Record<MeasurementType, UserMeasurementLog[]>> = {};
      for (const row of rows) {
        const list = byType[row.measurement_type];
        if (list) list.push(row);
        else byType[row.measurement_type] = [row];
      }
      for (const type of Object.keys(byType) as MeasurementType[]) {
        byType[type]!.sort(chronologically);
      }
      return { date, byType };
    }
  );

  return groups.sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : 0
  );
};

// Rounded to the displayed precision BEFORE the zero-check, so a genuine 0.1
// change is not swallowed by float noise (80.6 - 80.5 = 0.09999...).
const formatChange = (
  current: number,
  previous: number | undefined,
  unit: string
): string | null => {
  if (!current || !previous) return null;
  const diff = Number((current - previous).toFixed(1));
  if (diff === 0) return null;
  return formatSignedOneDecimalWithUnit(diff, unit);
};

const formatValue = (value: number, unit: string): string =>
  formatOneDecimalWithUnit(value, unit);

/**
 * Flattens one date group into display rows, ordered weight → body fat → body
 * fields. Within a type the newest row comes first, and only that newest row
 * carries a change badge, compared against the previous date's newest row of
 * the same type.
 */
export const buildDisplayRows = (
  group: DatedMeasurementGroup,
  previous?: DatedMeasurementGroup
): MeasurementDisplayRow[] => {
  const rows: MeasurementDisplayRow[] = [];

  for (const type of MEASUREMENT_DISPLAY_ORDER) {
    const typeRows = [...(group.byType[type] ?? [])].reverse();
    if (typeRows.length === 0) continue;

    const unit = MEASUREMENT_TYPE_UNITS[type] ?? "";
    const previousValue = latestOf(previous?.byType[type]);
    const previousNumber = previousValue
      ? parseFloat(previousValue.value)
      : undefined;

    typeRows.forEach((row, index) => {
      const value = parseFloat(row.value);
      rows.push({
        key: `${type}-${row.id}`,
        label: MEASUREMENT_TYPE_LABELS[type] ?? type,
        value: formatValue(value, unit),
        change: index === 0 ? formatChange(value, previousNumber, unit) : null,
        notes: row.notes || null,
        attachments: (row.attachments ?? []).filter(
          (attachment) => !!attachment.full_path
        ),
      });
    });
  }

  return rows;
};
